import { parseItems } from './items.js'
import { MONEY_CEILING } from './budget.js'
import { STEP, stepsFor } from './steps.js'
import { isValidNational } from './phone.js'

/**
 * What each screen refuses to leave without, said in Tagalog.
 *
 * Mirrors the rules `POST /api/orders` enforces. The API stays the authority — this only
 * spares the customer a rejected submit, and it matters more here than it looks: **the
 * API returns one opaque sentence with no field-level detail.** A body it refuses cannot
 * be explained to the customer at all, so anything catchable has to be caught before the
 * request leaves.
 *
 * One validator per named step, because the wizard blocks per screen. A customer who
 * cannot continue must be told why *on the screen they are looking at*.
 */

/**
 * The field holds the ten national digits — the `+63` is printed beside it, not typed.
 *
 * `utils/phone.js` folds every pasted shape down to those ten on the way in, so by the
 * time a value reaches here it is either ten digits or it is not a number at all.
 */
const isMobile = (value) => isValidNational(value)

const trimmed = (value) => String(value ?? '').trim()

/** A `datetime-local` value as milliseconds, or `null` when the box is empty. */
const timeOf = (value) => {
  const text = trimmed(value)
  if (!text) return null

  const at = new Date(text).getTime()
  return Number.isFinite(at) ? at : null
}

const validators = {
  /** Answered by pressing a card, so this only guards a state that never happened. */
  [STEP.SERVICE]: (state) =>
    (state?.service === 'pabili' || state?.service === 'padala'
      ? {}
      : { service: 'Piliin kung ano ang kailangan mo.' }),

  [STEP.PICKUP]: (state) => {
    const errors = {}
    const source = trimmed(state?.pickupSource)

    if (state?.pickup?.lat == null) {
      errors.pickup = 'Piliin kung saan bibili o kukuha ang rider.'
    }

    if (!source) errors.pickupSource = 'Anong tindahan o lugar ito?'
    else if (source.length < 2) errors.pickupSource = 'Masyadong maikli para mahanap.'

    return errors
  },

  /**
   * Skippable in full — but not half-answered.
   *
   * **A name with no number used to be thrown away silently.** `toOrderBody` only sends a
   * pickup contact when there is a number to ring, so *"Aling Nena"* alone never left the
   * browser; the server then read the absent contact as the account holder, and the rider
   * opened the order to find the customer's own name and number at the pickup. The
   * customer had answered the question and the answer vanished.
   *
   * So the number is required **once a name is typed**, and not before. Leaving the whole
   * screen blank is still the ordinary case and still costs nothing.
   *
   * A mistyped number is an error for the same reason: a rider dialling it at the counter
   * is the entire point of the field.
   */
  [STEP.PICKUP_CONTACT]: (state) => {
    const name = trimmed(state?.pickupContactName)
    const phone = trimmed(state?.pickupContactPhone)

    if (phone && !isMobile(phone)) {
      return { pickupContactPhone: 'Sampung numero, nagsisimula sa 9. Hal. 9171234567.' }
    }

    if (name && !phone) {
      return { pickupContactPhone: 'Kailangan din ang numero ni ' + name + ', para may matawagan ang rider.' }
    }

    return {}
  },

  [STEP.ITEMS]: (state) =>
    (parseItems(state?.itemsText).length === 0
      ? { itemsText: 'Isulat kung ano ang kailangan mo. Isang linya bawat isa.' }
      : {}),

  [STEP.BUDGET]: (state) => {
    const errors = {}
    const estimateText = trimmed(state?.estimatedTotal)
    const budgetText = trimmed(state?.maxBudget)
    const estimate = Number(estimateText)
    const budget = Number(budgetText)

    if (!estimateText || Number.isNaN(estimate)) {
      errors.estimatedTotal = 'Magkano ang tantiya mong gastos?'
    } else if (estimate < 0) {
      errors.estimatedTotal = 'Hindi pwedeng negatibo.'
    } else if (estimate > MONEY_CEILING) {
      errors.estimatedTotal = 'Masyadong malaki ang halagang yan.'
    }

    if (!budgetText || Number.isNaN(budget)) {
      errors.maxBudget = 'Magkano ang pinakamataas na pwede mong gastusin?'
    } else if (budget < 0) {
      errors.maxBudget = 'Hindi pwedeng negatibo.'
    } else if (budget > MONEY_CEILING) {
      errors.maxBudget = 'Masyadong malaki ang halagang yan.'
    } else if (!errors.estimatedTotal && budget < estimate) {
      errors.maxBudget = 'Mas mababa ang budget mo sa tantiyang halaga.'
    }

    return errors
  },

  [STEP.DROPOFF]: (state) => {
    const errors = {}
    const phone = trimmed(state?.phoneNumber)

    if (state?.dropoff?.lat == null) {
      errors.dropoff = 'Piliin kung saan ihahatid.'
    }

    // Required, and it is this end's number: `phoneNumber` is what the rider rings on
    // arrival. The name beside it is optional and blank means the account holder.
    if (!phone) errors.phoneNumber = 'Ilagay ang numerong tatawagan ng rider.'
    else if (!isMobile(phone)) errors.phoneNumber = 'Sampung numero, nagsisimula sa 9. Hal. 9171234567.'

    return errors
  },

  /** Skippable. The API refuses a time already past, and a delivery before its own pickup. */
  [STEP.TIMING]: (state) => {
    const errors = {}
    const now = Date.now()
    const pickupAt = timeOf(state?.pickupTime)
    const deliveryAt = timeOf(state?.deliveryTime)

    if (pickupAt != null && pickupAt < now) errors.pickupTime = 'Lumipas na ang oras na yan.'
    if (deliveryAt != null && deliveryAt < now) errors.deliveryTime = 'Lumipas na ang oras na yan.'

    if (pickupAt != null && deliveryAt != null && deliveryAt < pickupAt) {
      errors.deliveryTime = 'Hindi pwedeng mas maaga ang hatid kaysa sa kuha.'
    }

    return errors
  },

  [STEP.PAYMENT]: (state) =>
    (trimmed(state?.paymentMethod) ? {} : { paymentMethod: 'Piliin kung paano babayaran.' }),

  /** Reads back what the other steps set. It has no fields of its own to refuse. */
  [STEP.SUMMARY]: () => ({}),
}

/**
 * @param {string} step — a value from `STEP`
 * @param {object} state
 * @returns {Record<string, string>} — empty when the step may be left
 */
export function validateStep(step, state) {
  return validators[step] ? validators[step](state) : {}
}

/**
 * The earliest screen still holding a problem, or `null` when the order is placeable.
 *
 * Earliest rather than any, because sending somebody back to step 7 when step 2 is also
 * wrong means two trips. A step the current service does not have — the budget on a
 * padala — is not consulted at all.
 *
 * @returns {string|null}
 */
export function firstInvalidStep(state) {
  for (const step of stepsFor(state)) {
    if (Object.keys(validateStep(step, state)).length > 0) return step
  }

  return null
}
